import { useCallback, useEffect, useRef, type FC, type ReactNode } from 'react'

import { RatingPromptBottomSheet, type RatingPromptBottomSheetRef } from '@/components'
import { AppReviewContext } from '@/contexts'
import {
  loadReviewState,
  markPrompted,
  markRated,
  requestNativeReview,
  shouldPrompt,
  type ReviewState,
  type ReviewTrigger
} from '@/lib'

export interface AppReviewProviderProps {
  children: ReactNode
}

export const AppReviewProvider: FC<AppReviewProviderProps> = ({ children }) => {
  const sheetRef = useRef<RatingPromptBottomSheetRef>(null)
  const stateRef = useRef<ReviewState>({ rated: false, lastPromptedAt: null })
  const loadedRef = useRef(false)
  const shownThisSessionRef = useRef(false)
  const ratedThisPromptRef = useRef(false)

  useEffect(() => {
    void loadReviewState()
      .then((state) => {
        stateRef.current = state
      })
      .catch((error) => console.error('[appReview] loadReviewState failed', error))
      .finally(() => {
        loadedRef.current = true
      })
  }, [])

  const requestReviewPrompt = useCallback((_trigger: ReviewTrigger) => {
    if (!loadedRef.current || shownThisSessionRef.current) {
      return
    }
    if (!shouldPrompt(stateRef.current, Date.now())) {
      return
    }

    shownThisSessionRef.current = true
    ratedThisPromptRef.current = false
    sheetRef.current?.present()
  }, [])

  const handleRate = useCallback(() => {
    ratedThisPromptRef.current = true
    stateRef.current = { ...stateRef.current, rated: true }
    void markRated()
    void requestNativeReview().catch((error) => console.error('[appReview] requestReview failed', error))
    sheetRef.current?.dismiss()
  }, [])

  const handleNotNow = useCallback(() => {
    sheetRef.current?.dismiss()
  }, [])

  const handleDismiss = useCallback(() => {
    if (ratedThisPromptRef.current) {
      return
    }
    const now = Date.now()
    stateRef.current = { ...stateRef.current, lastPromptedAt: now }
    void markPrompted(now)
  }, [])

  return (
    <AppReviewContext.Provider value={{ requestReviewPrompt }}>
      {children}
      <RatingPromptBottomSheet
        onDismiss={handleDismiss}
        onNotNow={handleNotNow}
        onRate={handleRate}
        ref={sheetRef}
      />
    </AppReviewContext.Provider>
  )
}
